/** 智能报价 + 图像生成相关 API */
import { apiFetch } from "@/lib/api-client";
import type { SmartQuoteResponse, SessionTurn } from "@/types/api";

export interface SmartQuotePayload {
  text: string;
  session_id?: string;
}

export async function smartQuote(payload: SmartQuotePayload): Promise<SmartQuoteResponse> {
  // smart-quote 可能调 LLM, 给宽松一点超时
  return apiFetch<SmartQuoteResponse>("/api/smart-quote", {
    method: "POST",
    body: JSON.stringify(payload),
    timeoutMs: 60_000,
  });
}

export interface SessionHistory {
  session_id: string;
  turns: SessionTurn[];
}

export async function fetchSession(sessionId: string): Promise<SessionHistory> {
  return apiFetch<SessionHistory>(`/api/smart-quote/session/${encodeURIComponent(sessionId)}`);
}

/** 删除服务端会话记录。注意: 「新对话」不调用它 (与旧版一致, 只在本地换新会话),
 *  否则该对话会从问答记录里消失。 */
export async function clearSession(sessionId: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`/api/smart-quote/session/${encodeURIComponent(sessionId)}`, {
    method: "DELETE",
  });
}

// ── 图像生成 ────────────────────────────────────────────────────
export interface ParseImageRequestPayload {
  text: string;
  history?: Array<{ role: string; content: string }>;
}

/** 对应 prices_main.py /api/parse-image-request 的三种返回:
 *  - 失败:     { success: false, needs_size: false, error }
 *  - 缺尺寸:   { success: true, needs_size: true, prompt, ask_message }
 *  - 信息完整: { success: true, needs_size: false, prompt, width_cm, height_cm } */
export interface ParseImageResponse {
  success: boolean;
  needs_size?: boolean;
  prompt?: string;
  ask_message?: string;
  width_cm?: number;
  height_cm?: number;
  error?: string;
}

export async function parseImageRequest(p: ParseImageRequestPayload): Promise<ParseImageResponse> {
  return apiFetch<ParseImageResponse>("/api/parse-image-request", {
    method: "POST",
    body: JSON.stringify(p),
    timeoutMs: 60_000,
  });
}

export interface GenerateImagePayload {
  prompt: string;
  width_cm: number;
  height_cm: number;
  watermark?: boolean;
}

/** ark_service.generate_image: { success, images: [{url} | {b64_json}], size: "2048x2048", error } */
export interface GenerateImageResponse {
  success: boolean;
  images?: Array<{ url?: string; b64_json?: string }>;
  size?: string;
  error?: string | null;
}

export async function generateImage(p: GenerateImagePayload): Promise<GenerateImageResponse> {
  return apiFetch<GenerateImageResponse>("/api/generate-image", {
    method: "POST",
    body: JSON.stringify(p),
    timeoutMs: 180_000, // 图像生成慢
  });
}

// ── 问答记录 ────────────────────────────────────────────────────
export interface SessionSummary {
  session_id: string;
  first_ts: string;
  last_ts: string;
  turn_count: number;
  preview: string | null;
  /** 会话里最后一次报价的合计; 没报过价为 null */
  quote_total: number | null;
  quote_count: number;
}

export async function listMySessions(limit = 50): Promise<{ success: boolean; data: SessionSummary[] }> {
  return apiFetch<{ success: boolean; data: SessionSummary[] }>(
    `/api/smart-quote/sessions?limit=${limit}`,
  );
}
